import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useKeycloak } from "@react-keycloak/web";
import i18n from "@repo/i18n";
import Layout from "@repo/legacy/components/layout/Layout";
import Loader from "@repo/legacy/components/shared/Loader";
import { VersionCheckProvider } from "@repo/shared/components";
import { getImpersonation, setImpersonation } from "@repo/server-api/impersonation";
import fetch from "cross-fetch";
import { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";

import { menuItems } from "./layout/MenuData";
import AppRoutes from "./routes/AppRoutes";
import { RuleFactory } from "./rules/RuleFactory";

i18n.init();

const App = (props: { host: string }) => {
	const { keycloak, initialized } = useKeycloak();

	useEffect(() => {
		if (!initialized || !keycloak) return;

		const refreshInterval = setInterval(() => {
			keycloak
				.updateToken(60)
				.then((refreshed: boolean) => {
					if (refreshed) {
						console.log("Token refreshed");
					}
				})
				.catch(() => {
					console.warn("Token refresh failed, logging out...");
					keycloak.logout();
				});
		}, 6000);

		return () => clearInterval(refreshInterval);
	}, [initialized, keycloak]);

	// Listen for impersonation start messages from opener window
	useEffect(() => {
		let handled = false;

		const handleMessage = (event: MessageEvent) => {
			// Only accept messages from our origin
			if (event.origin !== window.location.origin) return;

			// Only accept from the window that opened us (the impersonation page)
			if (!event.source || event.source !== window.opener) return;

			// Only accept one impersonation message per window lifecycle
			if (handled) return;

			if (
				event.data &&
				typeof event.data === "object" &&
				event.data.type === "START_IMPERSONATION" &&
				event.data.state
			) {
				handled = true;
				setImpersonation(event.data.state);
				// Reload to apply impersonation headers
				window.location.reload();
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, []);

	const httpLink = useMemo(() => {
		return createHttpLink({ uri: props.host, fetch: fetch });
	}, [props.host]);

	const authLink = useMemo(() => {
		return setContext(async (_, { headers }) => {
			const token = keycloak?.token;
			const impersonation = getImpersonation();

			return {
				headers: {
					...headers,
					authorization: token ? `Bearer ${token}` : "",
					...(impersonation &&
						impersonation.targetUserId && {
							"X-Impersonate-User-Id": impersonation.targetUserId,
						}),
					...(impersonation &&
						!impersonation.targetUserId &&
						impersonation.targetContactId && {
							"X-Impersonate-Contact-Id": impersonation.targetContactId,
						}),
				},
			};
		});
	}, [keycloak]);

	const apolloClient = useMemo(() => {
		return new ApolloClient({
			link: authLink.concat(httpLink),
			cache: new InMemoryCache({ addTypename: false }),
		});
	}, [authLink, httpLink]);

	if (!initialized || !keycloak) {
		return <Loader />;
	}

	return (
		<ApolloProvider client={apolloClient}>
			<VersionCheckProvider>
				<BrowserRouter>
					<Layout menuItems={menuItems} ruleFactory={RuleFactory}>
						<AppRoutes />
					</Layout>
				</BrowserRouter>
			</VersionCheckProvider>
		</ApolloProvider>
	);
};

export default App;
